import { yieldToMainThread } from '@theme/utilities';
import { Component } from '@theme/component';

/**
 * Returns true when the element is rendered and takes up space on screen.
 * @param {Element | null | undefined} element
 * @returns {boolean}
 */
function isVisible(element) {
  if (!element) return false;
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

/**
 * Picks the cart the shopper can actually see: the floating bottom bar cart on phones,
 * otherwise the header cart icon.
 * @param {Element | null | undefined} fallback
 * @returns {Element}
 */
export function resolveFlyToCartDestination(fallback) {
  const floatingCart = document.querySelector('.mobile-bottom-nav__item:has([data-mbn-count]) .svg-wrapper');
  if (isVisible(floatingCart)) return /** @type {Element} */ (floatingCart);

  const headerCart = document.querySelector('.header-actions__cart-icon');
  if (isVisible(headerCart)) return /** @type {Element} */ (headerCart);

  return /** @type {Element} */ (fallback || floatingCart || headerCart);
}

/**
 * FlyToCart custom element for animating product images to cart
 * This component creates a visual effect of a product "flying" to the cart when added
 */
class FlyToCart extends Component {
  /** @type {Element} */
  source;

  /** @type {boolean} */
  useSourceSize = false;

  /** @type {Element} */
  destination;

  connectedCallback() {
    super.connectedCallback();
    this.destination = resolveFlyToCartDestination(this.destination);

    if (!this.source || !this.destination) {
      this.remove();
      return;
    }

    const intersectionObserver = new IntersectionObserver((entries) => {
      /** @type {DOMRectReadOnly | null} */
      let sourceRect = null;
      /** @type {DOMRectReadOnly | null} */
      let destinationRect = null;

      entries.forEach((entry) => {
        if (entry.target === this.source) {
          sourceRect = entry.boundingClientRect;
        } else if (entry.target === this.destination) {
          destinationRect = entry.boundingClientRect;
        }
      });

      if (sourceRect && destinationRect) {
        this.#animate(sourceRect, destinationRect);
      }

      intersectionObserver.disconnect();
    });
    intersectionObserver.observe(this.source);
    intersectionObserver.observe(this.destination);
  }

  /**
   * Animates the flying thingy along the bezier curve.
   * @param {DOMRectReadOnly} sourceRect - The bounding client rect of the source.
   * @param {DOMRectReadOnly} destinationRect - The bounding client rect of the destination.
   */
  #animate = async (sourceRect, destinationRect) => {
    //Define bezier curve points
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2,
      y: sourceRect.top + sourceRect.height / 2,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2,
      y: destinationRect.top + destinationRect.height / 2,
    };

    // Flying downwards (to the floating bottom cart): land exactly on the icon
    if (endPoint.y > startPoint.y) {
      this.style.setProperty('--offset-y', '0px');
    }

    // Position the flying thingy back to the start point
    if (this.useSourceSize) {
      this.style.setProperty('--width', `${sourceRect.width}px`);
      this.style.setProperty('--height', `${sourceRect.height}px`);
    }
    this.style.setProperty('--start-x', `${startPoint.x}px`);
    this.style.setProperty('--start-y', `${startPoint.y}px`);
    this.style.setProperty('--travel-x', `${endPoint.x - startPoint.x}px`);
    this.style.setProperty('--travel-y', `${endPoint.y - startPoint.y}px`);

    await yieldToMainThread();

    await Promise.allSettled(this.getAnimations().map((a) => a.finished));
    this.#bumpDestination();
    this.remove();
  };

  /**
   * Gives the cart a short bounce when the product lands, so the drop is easy to spot.
   */
  #bumpDestination() {
    const target = this.destination;
    if (!(target instanceof HTMLElement || target instanceof SVGElement)) return;

    target.classList.remove('fly-to-cart-bump');
    void (/** @type {HTMLElement} */ (target).offsetWidth);
    target.classList.add('fly-to-cart-bump');
    target.addEventListener('animationend', () => target.classList.remove('fly-to-cart-bump'), { once: true });
  }
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
